<script setup lang="ts">
import type { PreviewSegment } from '@/office/playbooks/suggestionPreview';
import { gaiaDiff, gaiaFontSize, gaiaNeutral } from '@/components/design/themes/tokens';

/**
 * A suggestion's redline, read the way the web run detail draws one: struck
 * text in the removed red, a pure insertion on the added green wash, and a
 * swapped-in replacement left plain because it reads against the strike
 * beside it.
 */
defineProps<{
  segments: PreviewSegment[];
}>();

const textColor = gaiaNeutral.text;
const textSize = gaiaFontSize.dense;
const removedColor = gaiaDiff.removedText;
const addedColor = gaiaDiff.addedText;
const addedSurface = gaiaDiff.addedSurface;
</script>

<template>
  <div class="preview">
    <span
      v-for="(segment, index) in segments"
      :key="index"
      :class="`segment segment--${segment.kind}`"
      >{{ segment.text }}</span
    >
  </div>
</template>

<style scoped>
/* The diff is one run of inline spans, so its type and the per-kind marks are
   CSS on the shared diff tokens — the same palette SuggestionRedlines uses. */
.preview {
  margin-top: 8px;
  min-width: 0;
  color: v-bind(textColor);
  font-size: v-bind(textSize);
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.segment--removed {
  color: v-bind(removedColor);
  text-decoration: line-through;
  text-decoration-thickness: 1.5px;
}

.segment--added {
  color: inherit;
  text-decoration: none;
}

.segment--added-extension {
  color: v-bind(addedColor);
  background: v-bind(addedSurface);
  border-radius: 3px;
  padding: 0 2px;
  text-decoration: none;
}
</style>
